import { createContext, useContext, useEffect, useMemo, useState } from "react";

export type Language = "en" | "ar";

/**
 * Interface chrome only.
 *
 * Long care prose is deliberately absent from here: it is the owner's own
 * Arabic, it renders verbatim in both modes, and it is never translated. A
 * paraphrase of a care instruction is the one kind of error that actually
 * harms a plant.
 */
const STRINGS = {
  en: {
    today: "Today",
    allPlants: "All plants",
    plants: "plants",
    plant: "plant",
    toTend: "to tend",
    outdoor: "Outdoor",
    indoor: "Indoor",
    nothingToCheck: "Nothing to check",
    nothingToCheckBody: "Every plant was checked recently enough. The next one comes round on its own.",
    checkSoil: "Check the soil",
    soilLastFelt: (reading: string, days: number) =>
      days === 0 ? `${reading} · today` : days === 1 ? `${reading} · yesterday` : `${reading} · ${days} days ago`,
    soilWateredNotFelt: (days: number) =>
      days === 0
        ? "watered today, not felt since"
        : days === 1
          ? "watered yesterday, not felt since"
          : `watered ${days} days ago, not felt since`,
    checkTheSoil: "Check the soil",
    soilJustFelt: (reading: string) => `${reading} · just now`,
    feedWith: "Feed with",
    notFeedingToday: "Not feeding today",
    worthMoving: "Worth moving",
    recordWhatYouDid: "Record what you did",
    howItIsDoing: "How it is doing",
    condition: "Condition",
    soil: "Soil",
    healthy: "Healthy",
    stressed: "Stressed",
    recovering: "Recovering",
    drainsFreely: "Drains freely",
    compacted: "Compacted",
    thingsToDo: "Things to do to this plant",
    history: "History",
    careGuide: "Care guide",
    light: "Light",
    checkDepth: "Check depth",
    watering: "Watering",
    pruning: "Pruning",
    warnings: "Warnings",
    add: "Add",
    when: "When",
    note: "Note",
    nothingLogged: "nothing logged for it yet",
    lastCheckedToday: "last checked today",
    lastCheckedYesterday: "last checked yesterday",
    lastCheckedDaysAgo: (days: number) => `last checked ${days} days ago`,
    noPlantsYet: "No plants yet",
    cm: "cm",
    language: "العربية",
    soilWet: "Wet",
    soilMoist: "Slightly moist",
    soilDry: "Dry",
    soilVeryDry: "Very dry",
    actWater: "Watered",
    actFeed: "Fed",
    actRepot: "Repotted",
    actPrune: "Pruned",
    actMist: "Misted",
    actRotate: "Rotated",
    actPropagate: "Propagated",
    actPestInspect: "Inspected for pests",
    actNote: "Note",
    actCheck: "Soil check",
    loggerHint: 'A soil check is worth logging even when you do not water — "still wet" is how the schedule learns this pot dries slowly.',
    notePlaceholder: "Anything worth remembering",
    whenHint: "Leave empty for just now. Set it to log something earlier.",
    stateNoteNotFed: "A plant that is not healthy is never fed.",
    stateNoteCompacted: "Compacted soil is checked less often.",
    stateNoteRepot: "Logging a repot clears the soil flag and holds feeding for six weeks.",
    todoPlaceholder: "Something to get round to",
    todoEmpty: "Nothing noted. Add anything you mean to get round to.",
    historyEmpty: "Nothing recorded yet. Anything logged above appears here and stays.",
    enteredLater: "entered later",
    viaTelegram: "via Telegram",
    compactedSoil: "compacted soil",
    edible: "Edible",
    toxic: "Toxic if chewed",
    wateringUnknown: "Last watering unknown",
    undo: "Undo",
    more: "More",
    waterItNow: "Water it now",
    whenNow: "Now",
    whenEarlier: "Earlier today",
    whenYesterday: "Yesterday",
    whenTwoDays: "2 days ago",
    correct: "Correct this",
    corrected: "corrected",
    voided: "this was a mistake",
    correctionLabel: "Correction",
    soilEndWet: "WET",
    soilEndDry: "DRY",
    daysOver: "days over",
    worthChecking: (due: number, total: number) => `${due} of ${total} worth checking`,
    notFeedingLede: "Each one shows the rule that stopped it.",
    feedAnyway: "Feed anyway",
    clearOn: "Clear on",
    nothingToCheckNext: "Next up",
    dayOneTitle: "Day one",
    dayOneBody: "Poke the soil of each one and say what you feel. From tomorrow the app only asks for the ones worth checking.",
    quietBody: "Every plant is inside its interval. The soil you poked yesterday is still doing its work.",
    why: "why",
    theSoilFeed: "the soil feed",
    theFoliarSpray: "the foliar spray",
    reasonAdjustedByChecks: "adjusted by this plant's soil checks",
    reasonOwnDryingTime: "using this plant's own drying time",
    reasonCompactedSoil: "compacted soil holds water longer",
    reasonLastWateringUnknown: "last watering unknown",
    reasonHeatSpike: (forecast: number, above: number) =>
      `checking earlier: ${forecast}°C forecast, ${above}°C above normal`,
    reasonOwnInterval: (days: number, place: string) =>
      `your own interval, ${days} days — the guide gives none for ${place.toLowerCase()}`,
    reasonNeverSprayedEdible:
      "never sprayed: you eat this plant and the label does not state it is safe on food crops",
    reasonNotWhileCondition: (state: string) =>
      `not while it is ${state.toLowerCase()} — feeding will not fix it, and may make it worse`,
    reasonRecentlyRepotted: "recently repotted — fresh soil already carries nutrients",
    reasonColdSeason: "not in the cold season — this plant is barely growing",
    reasonProductSeparation: (daysAgo: number, separation: number) =>
      `another product went on ${daysAgo} days ago — keep ${separation} days between them`,
    reasonOneAtATime: (chosen: string, separation: number) =>
      `one product at a time — ${chosen} goes on today, and the guide keeps ${separation} days between them`,
    reasonWaterFirst: "water it first, then feed — never onto dry roots",
    reasonFeedDue: (dose: string) => `due, at ${dose}`,
    reasonPlacementUnsuitable: (place: string) =>
      `the guide gives no watering interval for this plant ${place.toLowerCase() === "outdoor" ? "outdoors" : "indoors"}`,
    whereItLives: "Where it lives",
    whereItLivesNote: "Moving a plant changes how often the guide wants it checked, and starts its drying record again.",
    movedTo: "moved to",
    actMove: "Moved",
    actAccepted: "Kept here",
    samePlacement: "same kind of place",
    wrongPlaceTitle: "In the wrong place",
    wrongPlaceLede: "The guide has no interval for these, so nothing else on this screen will mention them.",
    moveBackTo: "Move back to",
    notSchedulable: "The guide cannot schedule it here",
    keepItHere: "Keep it here",
    keepItHereNoNumber: "Keep it here without one",
    howOftenToCheck: "How often should the app ask you to check it?",
    changeTheInterval: "Change how often it asks",
    nDays: (days: number) => `${days} days`,
    everyNDays: (days: number) => `checking every ${days} days`,
    ownIntervalNote: (days: number) =>
      `You are keeping it here, and the app asks every ${days} days — your figure, not the guide's.`,
    otherDays: "Other",
    use: "Use",
    keepingItHere: "keeping it here",
    guideCannotSchedule: "Your guide gives no check interval for this plant in this kind of place, so the app cannot say when to look at it.",
    keepingItHereNote: "You are keeping it here. The app has stopped asking about it — nothing else on Today will mention it.",
    moveBackInstead: "to undo this, move it back",
    dueToday: "due today",
    feedDue: "feed due",
    savedToHistory: "saved to its history",
    waterItNowBody: "soak until it runs from the base",
    wrongPlace: "wrong place",
  },
  ar: {
    today: "اليوم",
    allPlants: "كل النباتات",
    plants: "نباتات",
    plant: "نبات",
    toTend: "للعناية",
    outdoor: "بالخارج",
    indoor: "بالداخل",
    nothingToCheck: "لا شيء للفحص",
    nothingToCheckBody: "كل النباتات تم فحصها مؤخرًا. الموعد التالي سيأتي في وقته.",
    checkSoil: "افحص التربة",
    soilLastFelt: (reading: string, days: number) =>
      days === 0 ? `${reading} · اليوم` : days === 1 ? `${reading} · أمس` : `${reading} · منذ ${days} أيام`,
    soilWateredNotFelt: (days: number) =>
      days === 0
        ? "سُقي اليوم، ولم يُفحص بعده"
        : days === 1
          ? "سُقي أمس، ولم يُفحص بعده"
          : `سُقي منذ ${days} أيام، ولم يُفحص بعده`,
    checkTheSoil: "افحص التربة",
    soilJustFelt: (reading: string) => `${reading} · الآن`,
    feedWith: "سمّد بـ",
    notFeedingToday: "لا تسميد اليوم",
    worthMoving: "يُفضّل نقله",
    recordWhatYouDid: "سجّل ما فعلته",
    howItIsDoing: "كيف حاله",
    condition: "الحالة",
    soil: "التربة",
    healthy: "سليم",
    stressed: "مجهد",
    recovering: "يتعافى",
    drainsFreely: "تصريف جيد",
    compacted: "مكتومة",
    thingsToDo: "أشياء لعملها لهذا النبات",
    history: "السجل",
    careGuide: "دليل العناية",
    light: "الإضاءة",
    checkDepth: "عمق الفحص",
    watering: "الري",
    pruning: "التقليم",
    warnings: "تحذيرات",
    add: "إضافة",
    when: "متى",
    note: "ملاحظة",
    nothingLogged: "لا يوجد سجل بعد",
    lastCheckedToday: "تم فحصه اليوم",
    lastCheckedYesterday: "تم فحصه أمس",
    lastCheckedDaysAgo: (days: number) => `آخر فحص منذ ${days} يومًا`,
    noPlantsYet: "لا توجد نباتات بعد",
    cm: "سم",
    language: "English",
    soilWet: "رطبة",
    soilMoist: "رطبة قليلًا",
    soilDry: "جافة",
    soilVeryDry: "جافة جدًا",
    actWater: "سقيت",
    actFeed: "سمّدت",
    actRepot: "غيّرت الأصيص",
    actPrune: "قلّمت",
    actMist: "رششت ماء",
    actRotate: "لففت الأصيص",
    actPropagate: "أكثرت",
    actPestInspect: "فحصت الآفات",
    actNote: "ملاحظة",
    actCheck: "فحص التربة",
    loggerHint: "سجّل فحص التربة حتى لو لم تسقِ — إجابة «رطبة» هي ما يتعلّم منه الجدول أن هذا الأصيص يجف ببطء.",
    notePlaceholder: "أي شيء يستحق التذكّر",
    whenHint: "اتركه فارغًا للآن. اضبطه لتسجيل شيء حدث سابقًا.",
    stateNoteNotFed: "النبات غير السليم لا يُسمَّد.",
    stateNoteCompacted: "التربة المكتومة تُفحص على فترات أطول.",
    stateNoteRepot: "تسجيل تغيير الأصيص يلغي علامة التربة ويوقف التسميد ستة أسابيع.",
    todoPlaceholder: "شيء تنوي عمله",
    todoEmpty: "لا توجد ملاحظات. أضف أي شيء تنوي عمله.",
    historyEmpty: "لا يوجد سجل بعد. كل ما تسجّله أعلاه يظهر هنا ويبقى.",
    enteredLater: "أُدخل لاحقًا",
    viaTelegram: "عبر تيليجرام",
    compactedSoil: "تربة مكتومة",
    edible: "يؤكل",
    toxic: "سام عند المضغ",
    wateringUnknown: "آخر ري غير معروف",
    undo: "تراجع",
    more: "المزيد",
    waterItNow: "اسقِه الآن",
    whenNow: "الآن",
    whenEarlier: "سابقًا اليوم",
    whenYesterday: "أمس",
    whenTwoDays: "منذ يومين",
    correct: "تصحيح",
    corrected: "مُصحَّح",
    voided: "كان هذا خطأ",
    correctionLabel: "تصحيح",
    soilEndWet: "رطبة",
    soilEndDry: "جافة",
    daysOver: "يوم تأخير",
    worthChecking: (due: number, total: number) => `${due} من ${total} تستحق الفحص`,
    notFeedingLede: "كل واحد يوضّح القاعدة التي منعته.",
    feedAnyway: "سمّد على أي حال",
    clearOn: "متاح يوم",
    nothingToCheckNext: "التالي",
    dayOneTitle: "اليوم الأول",
    dayOneBody: "افحص تربة كل نبات وسجّل ما تشعر به. من الغد سيطلب التطبيق النباتات التي تستحق الفحص فقط.",
    quietBody: "كل النباتات داخل فترتها. التربة التي فحصتها أمس ما زالت تؤدي عملها.",
    why: "السبب",
    theSoilFeed: "سماد التربة",
    theFoliarSpray: "الرش الورقي",
    reasonAdjustedByChecks: "مضبوط حسب فحوص التربة لهذا النبات",
    reasonOwnDryingTime: "باستخدام زمن جفاف هذا النبات نفسه",
    reasonCompactedSoil: "التربة المكتومة تحتفظ بالماء وقتًا أطول",
    reasonLastWateringUnknown: "آخر ري غير معروف",
    reasonHeatSpike: (forecast: number, above: number) =>
      `الفحص أبكر: الحرارة المتوقعة ${forecast}°، أعلى من المعتاد بـ ${above}°`,
    reasonOwnInterval: (days: number, place: string) =>
      `فترتك أنت، ${days} يومًا — الدليل لا يعطي فترة ${place}`,
    reasonNeverSprayedEdible:
      "لا يُرش أبدًا: أنت تأكل هذا النبات والملصق لا يؤكد أنه آمن على المحاصيل الغذائية",
    reasonNotWhileCondition: (state: string) =>
      `ليس وهو ${state} — التسميد لن يصلحه وقد يزيد الأمر سوءًا`,
    reasonRecentlyRepotted: "غُيّر أصيصه حديثًا — التربة الجديدة تحمل غذاءً بالفعل",
    reasonColdSeason: "ليس في الموسم البارد — نموه شبه متوقف",
    reasonProductSeparation: (daysAgo: number, separation: number) =>
      `منتج آخر استُخدم منذ ${daysAgo} أيام — اترك ${separation} أيام بينهما`,
    reasonOneAtATime: (chosen: string, separation: number) =>
      `منتج واحد في المرة — ${chosen} اليوم، والدليل يترك ${separation} أيام بينهما`,
    reasonWaterFirst: "اسقِ أولًا ثم سمّد — أبدًا على جذور جافة",
    reasonFeedDue: (dose: string) => `مستحق، بـ ${dose}`,
    reasonPlacementUnsuitable: (place: string) =>
      `الدليل لا يعطي فترة ري لهذا النبات ${place}`,
    whereItLives: "أين يعيش",
    whereItLivesNote: "نقل النبات يغيّر عدد أيام الفحص التي يحددها الدليل، ويبدأ سجل جفافه من جديد.",
    movedTo: "نُقل إلى",
    actMove: "نُقل",
    actAccepted: "أُبقي هنا",
    samePlacement: "نفس نوع المكان",
    wrongPlaceTitle: "في المكان الخطأ",
    wrongPlaceLede: "الدليل لا يعطي فترة فحص لها هنا، فلن يذكرها أي شيء آخر في هذه الشاشة.",
    moveBackTo: "انقله إلى",
    notSchedulable: "الدليل لا يجدول هذا النبات هنا",
    keepItHere: "أبقِه هنا",
    keepItHereNoNumber: "أبقِه هنا بدون تحديد",
    howOftenToCheck: "كل كام يوم يطلب منك التطبيق فحصه؟",
    changeTheInterval: "غيّر عدد الأيام",
    nDays: (days: number) => `${days} يوم`,
    everyNDays: (days: number) => `الفحص كل ${days} يوم`,
    ownIntervalNote: (days: number) =>
      `أنت تُبقيه هنا، والتطبيق يسأل كل ${days} يوم — رقمك أنت، لا رقم الدليل.`,
    otherDays: "غير ذلك",
    use: "استخدم",
    keepingItHere: "أبقيه هنا",
    guideCannotSchedule: "دليلك لا يعطي فترة فحص لهذا النبات في هذا النوع من الأماكن، لذا لا يستطيع التطبيق تحديد موعد النظر إليه.",
    keepingItHereNote: "أنت تُبقيه هنا. توقّف التطبيق عن السؤال عنه — لن يذكره أي شيء آخر في شاشة اليوم.",
    moveBackInstead: "للتراجع، انقله مرة أخرى",
    dueToday: "مستحق اليوم",
    feedDue: "تسميد مستحق",
    savedToHistory: "حُفظ في سجله",
    waterItNowBody: "اسقِه حتى يخرج الماء من القاع",
    wrongPlace: "المكان غير مناسب",
  },
} as const;

export type Strings = (typeof STRINGS)["en"];

interface Localisation {
  language: Language;
  t: Strings;
  setLanguage: (language: Language) => void;
  /** Picks the right half of a bilingual short field. */
  pick: (text: { ar: string; en: string } | null | undefined) => string;
}

const Context = createContext<Localisation | null>(null);

const STORAGE_KEY = "planty.language";

function stored(): Language {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "ar" || saved === "en") return saved;
  } catch {
    // Private browsing, blocked storage: fall through to the default.
  }
  return "en";
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState<Language>(stored);

  useEffect(() => {
    // Arabic lays the whole interface out right to left.
    document.documentElement.lang = language;
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
    try {
      localStorage.setItem(STORAGE_KEY, language);
    } catch {
      // The choice simply will not persist. Nothing else breaks.
    }
  }, [language]);

  const value = useMemo<Localisation>(
    () => ({
      language,
      t: STRINGS[language] as Strings,
      setLanguage,
      pick: (text) => (text ? (language === "ar" ? text.ar : text.en) || text.en || text.ar : ""),
    }),
    [language],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useLanguage(): Localisation {
  const value = useContext(Context);
  if (!value) throw new Error("useLanguage must be used inside LanguageProvider");
  return value;
}

export function LanguageToggle() {
  const { language, t, setLanguage } = useLanguage();
  return (
    <button
      type="button"
      className="nav-pill lang"
      onClick={() => setLanguage(language === "en" ? "ar" : "en")}
    >
      {t.language}
    </button>
  );
}
